import {Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {UntypedFormGroup} from '@angular/forms';
import {POIParkingStationEntry} from './model/poi-parking-details.entry';
import {MapPosition} from '../../route/metamodel';
import {TemplateDetailsEntry} from './properties/templates/model/template.entry';
import {LkwItem} from '../lkw-orders/model/lkw-item';
import {UnveroFuelStationsResponseEntry} from './model/unvero/unvero-fuel-stations-response.entry';
import {FuelingPoiEntry} from '../../../../../core/component/fueling-poi/model/fueling-poi.entry';
import {ProductPrice} from './properties/fuel-chart/fuel-calculation.utils';

export interface FuelStations {
  stations: FuelingPoiEntry[];
  display: boolean;
  priceToUse: ProductPrice;
}

export interface UnveroFuelStations {
  response: UnveroFuelStationsResponseEntry;
  display: boolean;
  fuel: number;
  adblue: number;
}

export interface MarkedRoadsTolls {
  tolls: number;
  uuid: string;
}

export interface FormWaypoints {
  form: UntypedFormGroup;
  templateWaypoints: boolean;
}

export interface ReloadFormRequest {
  shouldRecenter: boolean;
  formIdx: number;
  forms: UntypedFormGroup[];
}

export type ContactlessType = 'CRT' | 'CIRCLE_K' | 'SHELL' | 'VIADA' | 'AS24' | 'null' | 'undefined' | null | undefined;

export type ParkingType = 'BOSCH' | 'TRAVIS' | null | undefined | 'null' | 'undefined';

export interface PoiInsertTask {
  lat: number;
  lng: number;
  name?: string;
  address?: string;
  lastPosition?: boolean;
  externalId?: string;
  poiId?: number;
  contactlessType?: ContactlessType;
  parkingType?: ParkingType;
  comment?: string;
  type?: string;
  emails?: string;
  importSelected?: boolean;
  unveroData?: any;
  lkwItem?: LkwItem;
  lkwOrderNumber?: string;
  position?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProTasksFormStateService {

  private formsSource = new Subject<UntypedFormGroup[]>();
  private forms$ = this.formsSource.asObservable();

  private loadFormSource = new Subject<UntypedFormGroup[]>();
  private loadForms$ = this.loadFormSource.asObservable();

  private reloadAllSource = new Subject<ReloadFormRequest>();
  private reloadAllForms$ = this.reloadAllSource.asObservable();

  private appendFromTemplateSource = new Subject<TemplateDetailsEntry[]>();
  private appendFromTemplate$ = this.appendFromTemplateSource.asObservable();

  private reloadFormSource = new Subject<FormWaypoints>();
  private reloadForms$ = this.reloadFormSource.asObservable();

  private routeChangeSource = new Subject<UntypedFormGroup>();
  private routeChange$ = this.routeChangeSource.asObservable();

  private fuelStationsSource = new Subject<FuelStations>();
  private fuelStations$ = this.fuelStationsSource.asObservable();

  private unveroFuelStationsSource = new Subject<UnveroFuelStations>();
  private unveroFuelStations$ = this.unveroFuelStationsSource.asObservable();

  private reloadFuelStationsSource = new Subject<void>();
  private reloadFuelStations$ = this.reloadFuelStationsSource.asObservable();

  private reloadFuelSource = new Subject<UntypedFormGroup[]>();
  private reloadFuel$ = this.reloadFuelSource.asObservable();

  private activeFormSource = new Subject<UntypedFormGroup>();
  private activeForm$ = this.activeFormSource.asObservable();

  private parkingStationsSource = new Subject<POIParkingStationEntry[]>();
  private parkingStations$ = this.parkingStationsSource.asObservable();

  private clickStationSource = new Subject<number>();
  private clickStation$ = this.clickStationSource.asObservable();

  private paramSwitchSource = new Subject<string>();
  private paramSwitch$ = this.paramSwitchSource.asObservable();

  private reloadParkingsSource = new Subject<UntypedFormGroup[]>();
  private reloadParkings$ = this.reloadParkingsSource.asObservable();

  private generateRouteInfoSource = new Subject<UntypedFormGroup>();
  private generateRouteInfo$ = this.generateRouteInfoSource.asObservable();

  private insertTaskSource = new Subject<PoiInsertTask>();
  private insertTask$ = this.insertTaskSource.asObservable();

  private insertRestMarkersSource = new Subject<any>();
  private insertRestMarkers$ = this.insertRestMarkersSource.asObservable();

  private optimalRouteSource = new Subject<void>();
  private optimalRoute$ = this.optimalRouteSource.asObservable();

  private fuelMarkerSource = new Subject<MapPosition>();
  private fuelMarker$ = this.fuelMarkerSource.asObservable();

  private trainOrFerryRemovalSource = new Subject<string>();
  private trainOrFerryRemoval$ = this.trainOrFerryRemovalSource.asObservable();

  private recalculateRouteInfoSource = new Subject<void>();
  private recalculateRouteInfo$ = this.recalculateRouteInfoSource.asObservable();

  private cabotageWarningSource = new Subject<boolean>();
  private cabotageWarning$ = this.cabotageWarningSource.asObservable();

  private markedPaidRoadsTollsSource = new Subject<MarkedRoadsTolls>();
  private markedPaidRoadsTolls$ = this.markedPaidRoadsTollsSource.asObservable();

  private mapRecenterSource = new Subject<string>();
  private mapRecenter$ = this.mapRecenterSource.asObservable();

  private reloadTaskMarkerSource = new Subject<UntypedFormGroup>();
  private reloadTaskMarker$ = this.reloadTaskMarkerSource.asObservable();

  private toggleAiRoutingSource = new Subject<boolean>();
  private toggleAiRouting$ = this.toggleAiRoutingSource.asObservable();

  private tasksFormSaveEnableSource = new Subject<void>();
  private tasksFormSaveEnable$ = this.tasksFormSaveEnableSource.asObservable();

  constructor() {
  }

  public postForms(forms: UntypedFormGroup[]): void {
    this.formsSource.next(forms);
  }

  public loadAllForms(): Observable<UntypedFormGroup[]> {
    return this.forms$;
  }

  public loadForm(forms: UntypedFormGroup[]): void {
    this.loadFormSource.next(forms);
  }

  public loadForms(): Observable<UntypedFormGroup[]> {
    return this.loadForms$;
  }

  public reloadAll(request: ReloadFormRequest): void {
    this.reloadAllSource.next(request);
  }

  public reloadAllForms(): Observable<ReloadFormRequest> {
    return this.reloadAllForms$;
  }

  public reloadForm(form: UntypedFormGroup, templateWaypoints = false): void {
    this.reloadFormSource.next({form, templateWaypoints});
  }

  public reloadForms(): Observable<FormWaypoints> {
    return this.reloadForms$;
  }

  public triggerAppendFromTemplate(details: TemplateDetailsEntry[]): void {
    this.appendFromTemplateSource.next(details);
  }

  public appendFromTemplate(): Observable<TemplateDetailsEntry[]> {
    return this.appendFromTemplate$;
  }

  public routeChanged(form: UntypedFormGroup): void {
    this.routeChangeSource.next(form);
  }

  public routeChanges(): Observable<UntypedFormGroup> {
    return this.routeChange$;
  }

  public triggerFuelStations(stations: FuelStations): void {
    this.fuelStationsSource.next(stations);
  }

  public fuelStations(): Observable<FuelStations> {
    return this.fuelStations$;
  }

  public triggerUnveroFuelStations(stations: UnveroFuelStations): void {
    this.unveroFuelStationsSource.next(stations);
  }

  public unveroFuelStations(): Observable<UnveroFuelStations> {
    return this.unveroFuelStations$;
  }

  public triggerEnableTasksFormsSave(): void {
    this.tasksFormSaveEnableSource.next();
  }

  public enableTasksFormsSave(): Observable<void> {
    return this.tasksFormSaveEnable$;
  }

  public triggerReloadFuelStations(): void {
    this.reloadFuelStationsSource.next();
  }

  public reloadFuelStations(): Observable<void> {
    return this.reloadFuelStations$;
  }

  public triggerReloadFuel(forms: UntypedFormGroup[]): void {
    this.reloadFuelSource.next(forms);
  }

  public reloadFuel(): Observable<UntypedFormGroup[]> {
    return this.reloadFuel$;
  }

  public triggerActiveForm(form: UntypedFormGroup): void {
    this.activeFormSource.next(form);
  }

  public activeForm(): Observable<UntypedFormGroup> {
    return this.activeForm$;
  }

  public triggerParkingStations(stations: POIParkingStationEntry[]): void {
    this.parkingStationsSource.next(stations);
  }

  public parkingStations(): Observable<POIParkingStationEntry[]> {
    return this.parkingStations$;
  }

  public triggerClickOnStation(poiId: number): void {
    this.clickStationSource.next(poiId);
  }

  public clickOnStation(): Observable<number> {
    return this.clickStation$;
  }

  public triggerParamsSwitch(paramType: string): void {
    this.paramSwitchSource.next(paramType);
  }

  public paramsSwitch(): Observable<string> {
    return this.paramSwitch$;
  }

  public triggerReloadParkings(forms: UntypedFormGroup[]): void {
    this.reloadParkingsSource.next(forms);
  }

  public reloadParkings(): Observable<UntypedFormGroup[]> {
    return this.reloadParkings$;
  }

  public triggerGenerateRouteInfo(form: UntypedFormGroup) {
    this.generateRouteInfoSource.next(form);
  }

  public generateRouteInfo(): Observable<UntypedFormGroup> {
    return this.generateRouteInfo$;
  }

  public triggerInsertTaskFromPOI(data: PoiInsertTask): void {
    this.insertTaskSource.next(data);
  }

  public insertTaskFromPOI(): Observable<PoiInsertTask> {
    return this.insertTask$;
  }

  public triggerRestMarkers(data: any): void {
    this.insertRestMarkersSource.next(data);
  }

  public insertRestMarkers(): Observable<any> {
    return this.insertRestMarkers$;
  }

  public triggerOptimalRouteCalc(): void {
    this.optimalRouteSource.next();
  }

  public calcOptimalRoute(): Observable<void> {
    return this.optimalRoute$;
  }

  public triggerFuelMarker(data: MapPosition): void {
    this.fuelMarkerSource.next(data)
  }

  public drawFuelMarker(): Observable<MapPosition> {
    return this.fuelMarker$;
  }

  public triggerTrainOrFerryRemoval(data: string) {
    this.trainOrFerryRemovalSource.next(data);
  }

  public removeTrainOrFerryLine(): Observable<string> {
    return this.trainOrFerryRemoval$;
  }

  public triggerRecalculateRouteInfo() {
    this.recalculateRouteInfoSource.next();
  }

  public recalculateRouteInfo(): Observable<void> {
    return this.recalculateRouteInfo$;
  }

  public triggerCabotageWarning(showWarning: boolean) {
    this.cabotageWarningSource.next(showWarning);
  }

  public notifyAboutCabotageWarning(): Observable<boolean> {
    return this.cabotageWarning$;
  }

  public triggerMarkedPaidRoadsTolls(tollsData: MarkedRoadsTolls) {
    this.markedPaidRoadsTollsSource.next(tollsData);
  }

  public markedPaidRoadsTollsObservable(): Observable<MarkedRoadsTolls> {
    return this.markedPaidRoadsTolls$;
  }

  public triggerMapRecenter(data: string) {
    this.mapRecenterSource.next(data);
  }

  public mapRecenterObservable(): Observable<string> {
    return this.mapRecenter$;
  }

  public triggerReloadTaskMarker(form: UntypedFormGroup): void {
    this.reloadTaskMarkerSource.next(form);
  }

  public reloadTaskMarker(): Observable<UntypedFormGroup> {
    return this.reloadTaskMarker$;
  }

  public toggleAiRouting(state: boolean) {
    this.toggleAiRoutingSource.next(state);
  }

  public toggleAiRoutingListener(): Observable<boolean> {
    return this.toggleAiRouting$;
  }

}
